import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AppConfig } from 'src/services/api';
import { getAppConfig } from 'src/services/appConfigService';
import { ADMIN_DATA_CHANGED_EVENT } from 'src/admin';
import { WEB_APP_DATA_CHANGED_EVENT } from './appDataEvents';

type AppConfigContextValue = {
  config: AppConfig | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

const AppConfigContext = createContext<AppConfigContextValue | null>(null);

export function AppConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const c = await getAppConfig(true);
    setConfig(c);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAppConfig().then((c) => {
      if (!cancelled) {
        setConfig(c);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
    window.addEventListener(WEB_APP_DATA_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
      window.removeEventListener(WEB_APP_DATA_CHANGED_EVENT, handler);
    };
  }, [load]);

  const value: AppConfigContextValue = {
    config,
    loading,
    refresh: load,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig(): AppConfigContextValue {
  const ctx = useContext(AppConfigContext);
  if (ctx == null) {
    throw new Error('useAppConfig must be used within AppConfigProvider');
  }
  return ctx;
}

export function useAppConfigOptional(): AppConfigContextValue | { config: null; loading: true; refresh: () => Promise<void> } {
  const ctx = useContext(AppConfigContext);
  if (ctx != null) return ctx;
  return {
    config: null,
    loading: true,
    refresh: async () => {},
  };
}
